import type { LineStatus } from "./status";

/**
 * What "open" means for a request line. One definition, four call sites.
 *
 * It was three definitions. `requests.list({ openOnly: true })` and the
 * outstanding-requests report both counted requested + approved + back-ordered;
 * the chip on the Requests screen counted requested + approved. So the header
 * undercounted the list directly beneath it by exactly the back-ordered set,
 * and the Reports page — one tap away — stated a third number for the same
 * thing. A rehearsal driving the app as the client found 70 and 60 side by side
 * and stopped trusting both.
 *
 * A number that disagrees with the rows under it costs more than a missing
 * number ever would, so the set lives here and nowhere else.
 *
 * **Open means "this line still needs something to happen to it."** Back-ordered
 * qualifies: the clinic asked, nobody has delivered, and Gloria tracks back
 * orders precisely because they are the ones that go quiet and get forgotten.
 *
 * Deliberately NOT open: `fulfilled-warehouse` (picked, awaiting dispatch — it
 * is counted and shown separately, because it is the warehouse's problem rather
 * than the clinic's), `ordered-vendor`, `received`, `cancelled`.
 */
export const OPEN_LINE_STATUSES = [
  "requested",
  "approved",
  "back-ordered",
] as const satisfies readonly LineStatus[];

export const isOpenLineStatus = (status: string): boolean =>
  (OPEN_LINE_STATUSES as readonly string[]).includes(status);

/**
 * Sum a `requests.statusCounts` record down to the open total.
 *
 * Takes the record the query already returns, so a screen never has to restate
 * the arithmetic — restating it is how the three definitions drifted apart.
 */
export const countOpen = (counts: Record<string, number> | undefined): number =>
  counts === undefined
    ? 0
    : OPEN_LINE_STATUSES.reduce((total, status) => total + (counts[status] ?? 0), 0);
